import { AppointmentTypes } from 'common';
import { BookingWizardFlowTypes } from 'common/dist/infrastructure/modules/appointment/interfaces/AppointmentTypes';
import { useInjection } from 'inversify-react';
import { useEffect } from 'react';

import { WizardStep } from 'domain/entities/WizardStep';
import { AsyncHookResult, InjectableHook, useHookInjection } from 'domain/hooks';

import {
  BookingWizardNavigationHook,
  IBookingWizardNavigationHook,
} from './useCaseNavigateBookingWizard';
import { ExternalNavigationHook, IExternalNavigationHook } from './useCaseNavigateTo';

export enum PaymentDetailsError {
  Unknown = 'unknown',
  InvalidPaymentInfo = 'invalid_payment_info',
}

export interface PaymentDetailsForm {
  expirationDate: string;
  id: string;
  email: string;
  country: string;
  address: string;
  cardHolder: string;
  cardNumber: string;
  cardType: string;
  city: string;
  expMonth: string;
  expYear: string;
  postalCode: string;
  state: string;
  ccv: string;
}

export enum KnownCardTypes {
  AmericanExpress = 'americanExpress',
}

const navigateAfterPayment = (
  flowType: string | undefined,
  appointments: AppointmentTypes.Appointment[] | undefined,
  navigation: IBookingWizardNavigationHook extends () => infer R ? R : never,
) => {
  if (appointments && appointments.length > 0) {
    navigation.redirectToStep(WizardStep.confirmReservation);
    return;
  }
  switch (flowType) {
    case BookingWizardFlowTypes.Lesson:
    case BookingWizardFlowTypes.FirstLesson:
    case BookingWizardFlowTypes.LessonByCoach:
    case BookingWizardFlowTypes.LessonByDate:
    case BookingWizardFlowTypes.ClubFittingbyCoach:
      navigation.redirectToStep(WizardStep.chooseLessonTime);
      break;
    case BookingWizardFlowTypes.ClubFitting:
      navigation.redirectToStep(WizardStep.selectFittingStartTime);
      break;
    case BookingWizardFlowTypes.Simulator:
    case BookingWizardFlowTypes.Bowling:
      navigation.redirectToStep(WizardStep.reservationDate);
      break;
    default:
      navigation.redirectToStep(WizardStep.confirmReservation);
  }
};

export const PaymentDetailsAdapter = Symbol('PaymentDetailsAdapter');
export type IPaymentDetailsAdapter = InjectableHook<
  AsyncHookResult<unknown, PaymentDetailsError> & {
    updatePaymentData: (locationId: string, values: PaymentDetailsForm) => Promise<void>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    countries: Array<any>;
    accessToken: string | null;
    isSuccess: boolean;
    hasCreditCardOnFile: boolean;
    flowType: string | undefined;
    fetchUserData: () => Promise<void>;
    fetchedUserEmail: string;
    appointments: AppointmentTypes.Appointment[] | undefined;
  }
>;

const useCasePaymentDetails = () => {
  const bookingWizardNavigation = useHookInjection<IBookingWizardNavigationHook>(
    BookingWizardNavigationHook,
  );

  const externalNavigation = useHookInjection<IExternalNavigationHook>(ExternalNavigationHook);
  const adapter = useInjection<IPaymentDetailsAdapter>(PaymentDetailsAdapter);
  const {
    error,
    result,
    inProgress,
    updatePaymentData,
    countries,
    accessToken,
    isSuccess,
    hasCreditCardOnFile,
    flowType,
    fetchUserData,
    fetchedUserEmail,
    appointments,
  } = adapter();

  const handleNextStep = async (locationId: string, values: PaymentDetailsForm) => {
    await updatePaymentData(locationId, values);
    await fetchUserData();
    if (flowType === BookingWizardFlowTypes.ClubFittingbyCoach) {
      navigateAfterPayment(flowType, appointments, bookingWizardNavigation);
    }
  };

  const handlePrevStep = () => {
    if (hasCreditCardOnFile) {
      bookingWizardNavigation.redirectToStep(WizardStep.confirmReservation);
    } else {
      switch (flowType) {
        case BookingWizardFlowTypes.Lesson:
        case BookingWizardFlowTypes.FirstLesson:
        case BookingWizardFlowTypes.LessonByCoach:
        case BookingWizardFlowTypes.LessonByDate:
        case BookingWizardFlowTypes.ClubFittingbyCoach:
          bookingWizardNavigation.redirectToStep(WizardStep.chooseLessonTime);
          break;
        case BookingWizardFlowTypes.ClubFitting:
          bookingWizardNavigation.redirectToStep(WizardStep.selectFittingStartTime);
          break;
        case BookingWizardFlowTypes.Simulator:
        case BookingWizardFlowTypes.Bowling:
          bookingWizardNavigation.redirectToStep(WizardStep.sessionLength);
          break;
      }
    }
  };

  useEffect(() => {
    if (((!error && result) || isSuccess) && flowType !== BookingWizardFlowTypes.ClubFittingbyCoach) {
      navigateAfterPayment(flowType, appointments, bookingWizardNavigation);
    }
  }, [error, result]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!accessToken || accessToken === '') {
      if (externalNavigation) {
        externalNavigation.redirectToSignInPage();
      }
    }
  }, [accessToken, externalNavigation]);

  return {
    fetchedUserEmail,
    inProgress,
    error,
    result,
    countryList: countries,
    nextStep: handleNextStep,
    prevStep: handlePrevStep,
  };
};

export default useCasePaymentDetails;
